// MySQL stored-routine procedural local-variable completion helpers (ROUND2-C1.2).
//
// Detects when the cursor is inside a MySQL stored-routine body:
//   - `CREATE [DEFINER=…] PROCEDURE name([params]) BEGIN … END`
//   - `CREATE [DEFINER=…] FUNCTION name([params]) RETURNS type BEGIN … END`
//   - `CREATE [DEFINER=…] TRIGGER name … FOR EACH ROW BEGIN … END`
//
// Emits `CompletionCandidate[]` for the variable declarations, parameters,
// and session-wide user variables visible at the cursor via the MySQL routines
// grammar's `parseRoutineBody` + `resolveScopeMysqlRoutines`.
//
// Tier mapping (mirrors `resolveScopeMysqlRoutines` tier ordering):
//   - Parameters (innermost-via-CALL scope)   → tier 1
//   - Declared routine-local `DECLARE` vars    → tier 1
//   - Session-wide `@user` variables           → tier 1
//
// NOTE: All three MySQL scope tiers are mapped to the intellisense tier 1.
// The four-tier ordering within the routine scope (`parameter` >
// `innermost-local` > `outer-local` > `user-variable`) is already respected
// by `resolveScopeMysqlRoutines` which returns members sorted by tier
// precedence and source position. Mapping them all to intellisense tier 1
// ensures they surface BEFORE schema objects (tier 2+) in the suggestion list.
//
// Scope gaps (documented, not blocking):
//   - Cursor declarations are not surfaced by the current grammar slice.
//   - Condition declarations and handler-bound variables are out-of-scope.
//   - DELIMITER rewriting (`DELIMITER $$`) is NOT handled by the predicate
//     or by the grammar parser — both treat the first `END` at the outer
//     nesting depth as the routine close, which is correct for all standard
//     single-body routines and for `BEGIN…END$$` when DELIMITER is used.
//
// Bundle-not-loaded guard: `parseRoutineBody` uses a dynamic import of the
// vendored ANTLR-generated MySQL parser. Wrapped in try/catch to degrade
// gracefully when the bundle hasn't finished loading yet.

import type { CompletionCandidate } from '@/core/engines';
import { loadCompletionModule } from '@/features/intellisense/telemetry/providerCost';
import { scanDocument } from '@/features/sql-grammar/document/scanner';
import { tokenize, type Token } from '../context/tokenizer';
import { detectMysqlCursorNameSlot } from './mysqlCursorNameSlot';
import {
  detectMysqlEnclosingRoutineKind,
  findInBufferCursorNames,
  findInBufferProceduralNames,
} from './mysqlInBufferProceduralLocals';
import { detectMysqlWriteTargetSlot } from './mysqlWriteTargetSlot';
import { scopedProceduralParse } from './scopedProceduralParse';

const ENGINE = 'mysql' as const;

// ─── Procedural-body detection ────────────────────────────────────────────────

/**
 * Returns `true` when the cursor offset lies inside a MySQL stored-routine
 * body (`BEGIN … END` block of a `CREATE PROCEDURE / FUNCTION / TRIGGER`).
 *
 * Implementation: linear token scan from buffer start to the cursor,
 * tracking the most recent `CREATE [DEFINER=…] PROCEDURE|FUNCTION|TRIGGER`
 * header. Once the header's matching `BEGIN` is found and consumed, the
 * body-active flag is set. A nesting counter tracks inner `BEGIN … END`
 * pairs so only the outer `END` clears the flag.
 *
 * MySQL `DELIMITER $$` is treated conservatively: the predicate looks for
 * a top-level `BEGIN` keyword after the `PROCEDURE`/`FUNCTION`/`TRIGGER`
 * header without requiring a specific delimiter. This correctly handles the
 * common `BEGIN … END$$` pattern; rare edge cases where users embed a nested
 * SQL statement that contains a standalone `BEGIN` between the header and the
 * body's `BEGIN` are not guarded — such shapes are unusual in practice.
 */
export function isInsideMysqlRoutineBody(
  source: string,
  offset: number,
): boolean {
  // Bounded scan: only tokens before the cursor matter (the loop breaks at
  // the cursor, and the END look-ahead guards on `nextTok.start < offset`).
  // Tokenizing the whole buffer was wasted work on every keystroke.
  const tokens = tokenize(source, 0, offset, ENGINE);
  let bodyActive = false;
  let beginDepth = 0;

  for (let i = 0; i < tokens.length; i += 1) {
    const tok = tokens[i];
    if (!tok || tok.start >= offset) break;

    const upper = identUpper(tok);
    if (!upper) continue;

    if (bodyActive) {
      // MySQL block-scope openers that pair with a matching END:
      //   BEGIN … END          (bare END)
      //   CASE … END CASE      (END CASE)
      //   IF … END IF          (END IF)
      //   LOOP … END LOOP      (END LOOP)
      //   WHILE … END WHILE    (END WHILE)
      //   REPEAT … END REPEAT  (END REPEAT)
      //
      // F11: MySQL overloads `IF` — the ternary FUNCTION `IF(cond,a,b)` shares
      // the keyword token with the statement `IF … THEN … END IF`. The
      // function form has NO matching `END IF`, so counting it as a block
      // opener leaves `beginDepth` permanently > 0 and the routine body never
      // closes (its locals then leak into the following statement). Only count
      // `IF` when it is the STATEMENT `IF` — i.e. NOT immediately followed by
      // `(` (which marks the function call `IF(`).
      if (upper === 'IF') {
        const next = tokens[i + 1];
        const isFunctionIf = next?.type === 'lparen' && next.start < offset;
        if (!isFunctionIf) {
          beginDepth += 1;
        }
        continue;
      }
      // NOTE: `CASE` is similarly overloaded (searched/simple CASE expression
      // vs. CASE statement), but both forms close with `END CASE`, so the
      // compound-close look-ahead below keeps the count balanced for the
      // statement form. The CASE *expression* (`CASE WHEN … END` inside a
      // SET/SELECT) closes with a bare `END` and would under-count by one;
      // that edge is not handled here (verified scope is the `IF()` case).
      if (
        upper === 'BEGIN' ||
        upper === 'CASE' ||
        upper === 'LOOP' ||
        upper === 'WHILE' ||
        upper === 'REPEAT'
      ) {
        beginDepth += 1;
        continue;
      }
      if (upper === 'END') {
        // Peek at the next token. If it is a compound-statement modifier
        // keyword (IF / LOOP / WHILE / REPEAT / CASE) AND that token lies
        // before the cursor, the `END <mod>` pair closes the corresponding
        // opener — consume both tokens together and decrement depth for the
        // opener. Otherwise treat the `END` as a bare closer for a `BEGIN`.
        const nextTok = tokens[i + 1];
        const nextUpper =
          nextTok && nextTok.start < offset ? identUpper(nextTok) : null;
        const isCompoundClose =
          nextUpper === 'IF' ||
          nextUpper === 'LOOP' ||
          nextUpper === 'WHILE' ||
          nextUpper === 'REPEAT' ||
          nextUpper === 'CASE';
        if (isCompoundClose) {
          // Advance past the modifier token so it isn't re-processed as an
          // opener on the next iteration.
          i += 1;
        }
        if (beginDepth > 0) {
          beginDepth -= 1;
        } else {
          // Top-level END (bare or compound) — closes the current routine body.
          bodyActive = false;
          beginDepth = 0;
        }
        continue;
      }
      if (upper === 'CREATE' || upper === 'ALTER') {
        // A new DDL statement resets the body unconditionally.
        bodyActive = false;
        beginDepth = 0;
        // Fall through to re-evaluate as a potential new routine header.
      }
    }

    if (!bodyActive) {
      if (upper === 'CREATE' || upper === 'ALTER') {
        // Skip optional DEFINER=… clause: `DEFINER` `=` `value` (3 tokens).
        const afterHeader = skipDefinerClause(tokens, i + 1);
        const kindTok = tokens[afterHeader];
        const kindUpper = identUpper(kindTok);

        if (
          kindUpper === 'PROCEDURE' ||
          kindUpper === 'FUNCTION' ||
          kindUpper === 'TRIGGER' ||
          kindUpper === 'EVENT'
        ) {
          // Scan forward to find the top-level BEGIN keyword.
          const beginIdx = findNextBeginIdx(tokens, afterHeader + 1);
          if (beginIdx !== -1 && tokens[beginIdx]!.start < offset) {
            bodyActive = true;
            beginDepth = 0;
            i = beginIdx;
          }
          continue;
        }
      }
    }
  }

  return bodyActive;
}

// ─── Candidate emission ───────────────────────────────────────────────────────

/**
 * Emit `CompletionCandidate[]` for the variables, parameters, and user
 * variables visible at `offset` inside a MySQL stored-routine body.
 *
 * Calls `parseRoutineBody` (async, ANTLR-backed) from the
 * `sql-grammar-mysql-routines` grammar package, then
 * `resolveScopeMysqlRoutines` to obtain the in-scope scope members. Wrapped
 * in try/catch so a missing bundle produces an empty list without aborting
 * the full candidate build.
 */
export async function emitMysqlProceduralLocalCandidates(
  source: string,
  offset: number,
  out: CompletionCandidate[],
): Promise<void> {
  // Track names emitted by the ANTLR path so the text-walk fallback (below)
  // doesn't duplicate them. ANTLR carries richer detail (data type, mode);
  // when both paths see the same name, ANTLR's candidate wins.
  const emittedNames = new Set<string>();
  // Track names intentionally suppressed at this slot (e.g. IN params at
  // FETCH INTO / SET LHS write-target slots) — the text-walk fallback must
  // NOT resurrect them. Without this, removing p_min_rentals from the ANTLR
  // path is undone by the fallback that doesn't know about mode.
  const suppressedNames = new Set<string>();
  // Slot classification — when truthy, parameters with mode='in' are
  // suppressed because the autocomplete spec lists IN procedure params as
  // unactionable at assignment positions.
  const writeTargetSlot = detectMysqlWriteTargetSlot(source, offset);
  // The IN-filter applies only to PROCEDURE bodies. MySQL FUNCTION parameters
  // have no mode keyword in syntax — the grammar still tags them
  // `parameterMode='in'` by convention, but the spec (script.34 §6) treats
  // them as legitimate at write-target slots, so the filter must skip them.
  const enclosingRoutineKind = detectMysqlEnclosingRoutineKind(source, offset);
  const applyInFilter =
    writeTargetSlot !== null && enclosingRoutineKind === 'procedure';
  // Cursor-name slot — when truthy, OPEN / FETCH / CLOSE expects a declared
  // cursor identifier here (pinned by script.46 §6/§7). Cursors are NOT
  // assignable variables, so the variable-emission path skips them; we
  // surface them here as a dedicated kind so they only appear in slots
  // where they're legal.
  const cursorNameSlot = detectMysqlCursorNameSlot(source, offset);
  const emittedCursors = new Set<string>();
  try {
    const { parseRoutineBody } = await loadCompletionModule(
      '@/features/sql-grammar-mysql-routines/parse',
      () => import('@/features/sql-grammar-mysql-routines/parse'),
    );
    const { resolveScopeMysqlRoutines } = await loadCompletionModule(
      '@/features/sql-grammar-mysql-routines/resolver/resolveScopeMysqlRoutines',
      () => import('@/features/sql-grammar-mysql-routines/resolver/resolveScopeMysqlRoutines'),
    );

    // Statement/block-scoped parse (plan16 T4). Parse ONLY the routine
    // statement enclosing the cursor — not the whole buffer — and cache it by
    // slice text. This generalizes the old DELIMITER-only slicing (which was
    // the offset-translation precedent this task builds on) to EVERY mysql
    // routine shape: `mysqlStatementSlice` runs `scanDocument` with
    // `compoundBlockAware` so a `;`-bodied `BEGIN…END` stays one statement even
    // without a `DELIMITER` directive, and the DELIMITER path keeps its exact
    // prior boundaries (verified byte-identical). The `parse` callback re-
    // terminates the slice with `;` exactly as the old DELIMITER branch did —
    // a routine body needs a trailing terminator to parse cleanly.
    const parseResult = await scopedProceduralParse({
      dialect: 'mysql-routines',
      source,
      offset,
      sliceFor: mysqlStatementSlice,
      parse: (sliceText) =>
        parseRoutineBody(reterminateMysqlSlice(sliceText), { engine: 'mysql' }),
    });
    // Oversize guard (D6): a slice past MAX_MAIN_THREAD_SLICE_BYTES returns
    // null. Route the skip through the shared catch below so the text-walk
    // fallback still contributes degraded locals (better than freezing the main
    // thread on a huge buffer, which the pre-T4 full-buffer parse did).
    if (!parseResult) {
      throw new Error('mysql procedural slice guarded out (oversize)');
    }
    const parsed = parseResult.body;
    // Offset translation (T4 crux): the caret in slice-relative coordinates.
    // `parsed.source` is the re-terminated slice; appended `;`/newline sit
    // AFTER the caret, so `offset - sliceStart` stays valid against it.
    const parseSource = parsed.source;
    const parseOffset = offset - parseResult.sliceStart;
    const scope = resolveScopeMysqlRoutines(parsed, parseOffset);

    // E2E-only diagnostic hook. Gated on `window.__jamSqlE2eProceduralProbe__`
    // being a function — production is zero-cost. When installed, records the
    // intermediate parse + scope state for offline analysis of why DECLAREd
    // locals don't surface in the e2e (resolver-side vs Monaco-side drop).
    type ProbeInfo = {
      sourceLen: number;
      parseSourceLen: number;
      cursorOffset: number;
      parseOffset: number;
      didDelimiterRoute: boolean;
      statementKind: string;
      parsedStatementsCount: number;
      parsedParametersCount: number;
      recoveryConfidence: string;
      recoveryErrorCount: number;
      recoveryErrorRanges: ReadonlyArray<{ startOffset: number; endOffset: number; startLine: number; startColumn: number }>;
      parseSourceAroundFirstError: string | null;
      scopeVariablesCount: number;
      scopeVariableNames: ReadonlyArray<{
        kind: string;
        name: string;
        tier: string;
        visibleAtStart: number;
        visibleAtEnd: number;
      }>;
      parseTreeRootRule: number | null;
      parseTreeChildCount: number | null;
    };
    const probe = (globalThis as unknown as {
      __jamSqlE2eProceduralProbe__?: (info: ProbeInfo) => void;
    }).__jamSqlE2eProceduralProbe__;
    if (typeof probe === 'function') {
      try {
        const tree = parsed.parseTree as
          | { ruleIndex?: number; children?: unknown[] }
          | undefined;
        probe({
          sourceLen: source.length,
          parseSourceLen: parseSource.length,
          cursorOffset: offset,
          parseOffset,
          didDelimiterRoute: parseSource !== source,
          statementKind: parsed.statementKind ?? 'unknown',
          parsedStatementsCount: parsed.statements?.length ?? 0,
          parsedParametersCount: parsed.parameters?.length ?? 0,
          recoveryConfidence: parsed.recovery?.confidence ?? 'unknown',
          recoveryErrorCount: parsed.recovery?.errorRanges?.length ?? 0,
          recoveryErrorRanges: (parsed.recovery?.errorRanges ?? []).map((r) => ({
            startOffset: r.startOffset,
            endOffset: r.endOffset,
            startLine: r.startLine,
            startColumn: r.startColumn,
          })),
          parseSourceAroundFirstError:
            parsed.recovery?.errorRanges?.[0]
              ? parseSource.slice(
                  Math.max(0, parsed.recovery.errorRanges[0]!.startOffset - 30),
                  parsed.recovery.errorRanges[0]!.endOffset + 30,
                )
              : null,
          scopeVariablesCount: scope.variables.length,
          scopeVariableNames: scope.variables.map((m) => ({
            kind: m.kind,
            name: m.name,
            tier: m.tier,
            visibleAtStart: m.visibleAt.startOffset,
            visibleAtEnd: m.visibleAt.endOffset,
          })),
          parseTreeRootRule:
            typeof tree?.ruleIndex === 'number' ? tree.ruleIndex : null,
          parseTreeChildCount: Array.isArray(tree?.children)
            ? tree.children.length
            : null,
        });
      } catch {
        // Never break completion for a probe error.
      }
    }

    for (const member of scope.variables) {
      // Suppress IN-mode parameters at assignment-target slots
      // (FETCH INTO, SET LHS, SELECT INTO target). IN params are
      // read-only and cannot legally appear at these positions —
      // surfacing them produces unactionable suggestions that the
      // user's autocomplete spec explicitly forbids.
      if (
        applyInFilter &&
        member.kind === 'parameter' &&
        (member as { parameterMode?: string }).parameterMode === 'in'
      ) {
        suppressedNames.add(member.name);
        continue;
      }
      const { label, detail } = buildMemberLabel(member);
      out.push({
        label,
        insertText: label,
        isSnippet: false,
        kind: 'variable',
        tier: 1,
        detail,
        filterText: label,
      });
      emittedNames.add(member.name);
    }

    // Cursor names — emitted only at cursor-name slots (OPEN/FETCH/CLOSE)
    // so they don't leak into general-expression positions where cursor
    // identifiers are not legal.
    if (cursorNameSlot !== null) {
      const { extractDeclaredCursors } = await loadCompletionModule(
        '@/features/sql-grammar-mysql-routines/extractors/declaredCursors',
        () => import('@/features/sql-grammar-mysql-routines/extractors/declaredCursors'),
      );
      for (const cur of extractDeclaredCursors(parsed)) {
        if (parseOffset < cur.visibleAt.startOffset) continue;
        if (parseOffset > cur.visibleAt.endOffset) continue;
        if (emittedCursors.has(cur.name)) continue;
        out.push({
          label: cur.name,
          insertText: cur.name,
          isSnippet: false,
          kind: 'variable',
          tier: 1,
          detail: 'Cursor (DECLARE … CURSOR FOR)',
          filterText: cur.name,
        });
        emittedCursors.add(cur.name);
      }
    }
  } catch {
    // Bundle not yet loaded or parse failure — degrade gracefully.
    // The DML-side candidates (tables, columns, keywords) still flow.
  }

  // Cursor-name fallback — when the ANTLR bundle path didn't supply
  // cursors (bundle not loaded / parse failed) the text-walk extractor
  // still finds `DECLARE c CURSOR FOR …` shapes. De-duped by name.
  if (cursorNameSlot !== null) {
    try {
      for (const name of findInBufferCursorNames(source, offset)) {
        if (emittedCursors.has(name)) continue;
        out.push({
          label: name,
          insertText: name,
          isSnippet: false,
          kind: 'variable',
          tier: 1,
          detail: 'Cursor (DECLARE … CURSOR FOR)',
          filterText: name,
        });
        emittedCursors.add(name);
      }
    } catch {
      // Defensive only.
    }
  }

  // Text-walk fallback — runs unconditionally, contributes names the ANTLR
  // path lost to error-recovery (script.33 §11/§13, script.34 §6, script.46
  // §8 in the bundled renderer per MONACO_STATE_DIAG.md). De-duped by name
  // against the ANTLR output so the rich-detail ANTLR candidate wins when
  // both paths see the same name.
  try {
    const fallbackNames = findInBufferProceduralNames(source, offset);
    for (const n of fallbackNames) {
      if (emittedNames.has(n.name)) continue;
      // Honor the ANTLR-path mode-aware suppression so the fallback
      // doesn't resurrect IN params that the ANTLR path correctly
      // filtered out at a write-target slot.
      if (suppressedNames.has(n.name)) continue;
      // The fallback also tags IN-mode parameters when available
      // (via `parameterMode` on the result). Suppress those at
      // write-target slots even when the ANTLR path didn't run
      // (e.g. bundle not yet loaded / parse failed).
      if (
        applyInFilter &&
        n.kind === 'parameter' &&
        n.parameterMode === 'in'
      ) {
        continue;
      }
      out.push({
        label: n.name,
        insertText: n.name,
        isSnippet: false,
        kind: 'variable',
        tier: 1,
        detail:
          n.kind === 'parameter'
            ? 'Procedure parameter'
            : 'Local variable (DECLARE)',
        filterText: n.name,
      });
      emittedNames.add(n.name);
    }
  } catch {
    // Defensive: even an error here must not break DML candidate emission.
  }
}

// ─── Slice derivation (plan16 T4) ────────────────────────────────────────────

/**
 * Return the `[start, end)` range of the mysql routine statement that encloses
 * `offset`, or null (→ full source) when the caret sits outside every scanned
 * statement (e.g. trailing whitespace). `compoundBlockAware` keeps a
 * `;`-terminated `BEGIN…END` body as ONE statement even without a `DELIMITER`
 * directive; the `DELIMITER //` path yields the identical boundary it did
 * before this generalization (verified byte-for-byte). Exported for the slice
 * unit tests.
 */
export function mysqlStatementSlice(
  source: string,
  offset: number,
): { start: number; end: number } | null {
  const scan = scanDocument(source, { engine: 'mysql', compoundBlockAware: true });
  const stmt = scan.statements.find(
    (s) => s.startOffset <= offset && offset <= s.endOffset,
  );
  return stmt ? { start: stmt.startOffset, end: stmt.endOffset } : null;
}

/**
 * Re-terminate an extracted routine slice with a trailing `;` so
 * `parseRoutineBody` parses it cleanly (the statement scanner strips the
 * delimiter). Only appends after any existing terminator + trims trailing
 * whitespace — it NEVER inserts before the caret, so the caller's
 * `offset - sliceStart` translation stays valid. Identical to the logic the
 * old DELIMITER branch applied.
 */
export function reterminateMysqlSlice(sliceText: string): string {
  const trimmed = sliceText.replace(/\s+$/, '');
  return /;\s*$/.test(trimmed) ? `${trimmed}\n` : `${trimmed};\n`;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

type ScopeMember =
  | { kind: 'variable'; name: string; decl: { dataType: { raw: string } } }
  | { kind: 'parameter'; name: string; decl: { dataType: { raw: string } }; parameterMode: string }
  | { kind: 'user-variable'; name: string };

function buildMemberLabel(member: ScopeMember): { label: string; detail: string } {
  switch (member.kind) {
    case 'parameter':
      return {
        label: member.name,
        detail: `${member.decl.dataType.raw} (${member.parameterMode} parameter)`,
      };
    case 'variable':
      return {
        label: member.name,
        detail: `${member.decl.dataType.raw} (local variable)`,
      };
    case 'user-variable':
      return {
        label: member.name,
        detail: '@var (user variable)',
      };
  }
}

/**
 * Skip an optional `DEFINER = 'user'@'host'` clause that may appear between
 * `CREATE` and `PROCEDURE`/`FUNCTION`/`TRIGGER`. The clause is 3 tokens:
 * `DEFINER`, `=`, `<value>`. If the next token is not `DEFINER`, returns
 * `idx` unchanged.
 */
function skipDefinerClause(tokens: readonly Token[], idx: number): number {
  if (identUpper(tokens[idx]) === 'DEFINER') {
    // Skip: DEFINER = value (3 tokens). The value may itself be a
    // compound expression like `'root'@'localhost'` — scan to the next
    // non-operator / non-literal token that is an identifier.
    let j = idx + 1;
    // Skip `=`
    if (j < tokens.length && tokens[j]?.type === 'op') j += 1;
    // Skip the value token(s): `'user'@'host'` appears as multiple tokens.
    // Advance past any sequence of literals, ops, and idents that precede
    // the next reserved word (PROCEDURE / FUNCTION / TRIGGER / etc.).
    while (j < tokens.length) {
      const t = tokens[j];
      if (!t) break;
      if (t.type === 'literal' || t.type === 'op') {
        j += 1;
        continue;
      }
      if (t.type === 'ident' || t.type === 'kw') {
        const u = t.text.toUpperCase();
        if (
          u === 'PROCEDURE' ||
          u === 'FUNCTION' ||
          u === 'TRIGGER' ||
          u === 'EVENT'
        ) {
          break;
        }
        // Looks like the user-name identifier itself (e.g. bare `root`).
        j += 1;
        continue;
      }
      break;
    }
    return j;
  }
  return idx;
}

/**
 * Find the next `BEGIN` keyword token starting at `from`. Bounded to
 * the next 128 tokens to handle long parameter lists without scanning
 * across unrelated statements.
 */
function findNextBeginIdx(tokens: readonly Token[], from: number): number {
  const limit = Math.min(tokens.length, from + 128);
  for (let i = from; i < limit; i += 1) {
    if (identUpper(tokens[i]) === 'BEGIN') return i;
  }
  return -1;
}

function identUpper(t: Token | undefined): string | null {
  if (!t) return null;
  if (t.type !== 'ident' && t.type !== 'kw') return null;
  return t.text.toUpperCase();
}
